/**
 * Tutorial Part 2 — home-screen onboarding task board.
 *
 * A collapsible card that lives in HomeScreen's `.home-progression-slot`. Shows
 * 8 early-days tasks across 2 pages of 4; finishing a page grants a dice (the
 * reveal is driven from main.ts via the `tutorial2:updated` broadcast), and
 * finishing both pages unlocks daily-challenge eligibility, after which the
 * board retires.
 *
 * Pure visual representation of server state: `applyState` re-renders from the
 * authoritative `Tutorial2State`; the widget never tracks completion itself.
 * Row taps emit `task` with a deep-link target for main.ts to route.
 */

import { EventEmitter } from '../../engine/core/EventEmitter';
import { attachSwipePager } from './swipePager';
import {
  Tutorial2State,
  Tutorial2TaskId,
  TUTORIAL2_PAGE1_TASKS,
} from '@sdl/shared';

/** Where a task row deep-links to. Routed by main.ts. */
export type TaskTarget = 'daily-bonus' | 'play' | 'crew' | 'account' | 'dice';

interface TaskDef {
  id: Tutorial2TaskId;
  icon: string;
  title: string;
  desc: string;
  target: TaskTarget;
}

interface PageDef {
  title: string;
  tasks: TaskDef[];
}

const PAGES: PageDef[] = [
  {
    title: 'GET IN THE GAME',
    tasks: [
      { id: 'dailyBonus', icon: '🎁', title: 'Claim your daily bonus', desc: 'Open the case on the home screen.', target: 'daily-bonus' },
      { id: 'playCraps', icon: '🎲', title: 'Play a round of Street Craps', desc: 'Tap Play and join a craps table.', target: 'play' },
      { id: 'playCeelo', icon: '⚂', title: 'Play a round of Cee-Lo', desc: 'Tap Play and join a Cee-Lo table.', target: 'play' },
      { id: 'placeSidebet', icon: '💰', title: 'Place a sidebet', desc: "Back a bet on another shooter's roll.", target: 'play' },
    ],
  },
  {
    title: 'MAKE IT YOURS',
    tasks: [
      { id: 'setPfp', icon: '📷', title: 'Set your profile picture', desc: 'Add a photo from Settings.', target: 'account' },
      { id: 'addFriend', icon: '👥', title: 'Add a friend', desc: 'Search a name in Crew and tap Add.', target: 'crew' },
      { id: 'connectAccount', icon: '🔒', title: 'Secure your account', desc: 'Link Google or Apple for a bonus.', target: 'account' },
      { id: 'equipDice', icon: '✨', title: 'Equip a new dice', desc: 'Pick a set in the Dice screen.', target: 'dice' },
    ],
  },
];

const COLLAPSE_KEY = 'sdl_tb_collapsed';

export class TaskBoardWidget extends EventEmitter {
  public element: HTMLElement;
  private rowsEl: HTMLElement;
  private titleEl: HTMLElement;
  private progressEl: HTMLElement;
  private dotsEl: HTMLElement;
  private bodyEl: HTMLElement;
  private caretEl: HTMLElement;

  private state: Tutorial2State | null = null;
  private currentPage = 0;
  private userPicked = false;
  private collapsed = false;

  constructor() {
    super();
    this.injectStyles();
    this.collapsed = localStorage.getItem(COLLAPSE_KEY) === '1';

    this.element = document.createElement('div');
    this.element.className = 'sdl-taskboard';
    // Touches starting here never page the SCREEN pager ([data-no-swipe]
    // opt-out in UIManager); the board pages itself via attachSwipePager.
    this.element.dataset.noSwipe = 'true';
    this.element.style.display = 'none'; // hidden until applyState + setVisible
    this.element.innerHTML = `
      <button class="sdl-tb-header" type="button">
        <div class="sdl-tb-titlewrap">
          <span class="sdl-tb-kicker">FIRST STEPS</span>
          <span class="sdl-tb-title"></span>
        </div>
        <span class="sdl-tb-progress"></span>
        <span class="sdl-tb-caret">▾</span>
      </button>
      <div class="sdl-tb-body">
        <div class="sdl-tb-rows"></div>
        <div class="sdl-tb-foot">
          <div class="sdl-tb-dots"></div>
          <div class="sdl-tb-reward">🎲 Finish the page for a new dice</div>
        </div>
      </div>
    `;

    this.titleEl = this.element.querySelector('.sdl-tb-title')!;
    this.progressEl = this.element.querySelector('.sdl-tb-progress')!;
    this.rowsEl = this.element.querySelector('.sdl-tb-rows')!;
    this.dotsEl = this.element.querySelector('.sdl-tb-dots')!;
    this.bodyEl = this.element.querySelector('.sdl-tb-body')!;
    this.caretEl = this.element.querySelector('.sdl-tb-caret')!;

    this.element.querySelector('.sdl-tb-header')!.addEventListener('click', () => this.toggleCollapse());
    attachSwipePager(this.bodyEl, (dir) => this.swipePage(dir));
    this.applyCollapsed(false);
    // Foreground heal: rebuilding the rows forces the WebView to repaint the
    // subtree, recovering from any stale blank left while backgrounded.
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  private onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible' && this.state) this.render();
  };

  /** Swipe between pages with a small slide-in beat. */
  private swipePage(dir: 1 | -1): void {
    const next = this.currentPage + dir;
    if (next < 0 || next >= PAGES.length) return;
    this.userPicked = true;
    this.currentPage = next;
    this.render();
    const anim = this.rowsEl.animate(
      [
        { transform: `translateX(${dir * 18}px)`, opacity: 0.3 },
        { transform: 'translateX(0)', opacity: 1 },
      ],
      { duration: 200, easing: 'ease-out' },
    );
    // Samsung WebView can drop the box's paint when this animation's
    // temporary compositor layer is torn down (DOM + layout intact, rows
    // render blank). Dirty the element for one frame after the layer is
    // gone so the region is repainted in place.
    anim.onfinish = () => {
      this.rowsEl.style.opacity = '0.999';
      requestAnimationFrame(() => { this.rowsEl.style.opacity = ''; });
    };
  }

  /** Re-render from authoritative server state. Does NOT change visibility. */
  applyState(state: Tutorial2State): void {
    this.state = state;
    // Follow the first incomplete page unless the player navigated manually.
    if (!this.userPicked) {
      const p1Done = TUTORIAL2_PAGE1_TASKS.every(id => state[id]);
      this.currentPage = p1Done ? 1 : 0;
    }
    this.render();
  }

  setVisible(visible: boolean): void {
    this.element.style.display = visible ? '' : 'none';
  }

  /** End-of-board: both pages done. Fade + collapse out, then hide. */
  retire(): void {
    this.element.classList.add('sdl-tb-retiring');
    window.setTimeout(() => {
      this.setVisible(false);
      this.element.classList.remove('sdl-tb-retiring');
    }, 450);
  }

  private render(): void {
    if (!this.state) return;
    const state = this.state;
    const page = PAGES[this.currentPage];

    this.titleEl.textContent = page.title;
    const done = page.tasks.filter(t => state[t.id]).length;
    this.progressEl.textContent = `${done}/${page.tasks.length}`;

    this.rowsEl.innerHTML = '';
    for (const task of page.tasks) {
      const isDone = state[task.id];
      const row = document.createElement('button');
      row.type = 'button';
      row.className = `sdl-tb-row${isDone ? ' done' : ''}`;
      row.innerHTML = `
        <span class="sdl-tb-icon">${isDone ? '✓' : task.icon}</span>
        <span class="sdl-tb-text">
          <span class="sdl-tb-rowtitle">${task.title}</span>
          <span class="sdl-tb-rowdesc">${task.desc}</span>
        </span>
        <span class="sdl-tb-arrow">${isDone ? '' : '›'}</span>
      `;
      if (!isDone) {
        row.addEventListener('click', () => this.emit('task', { task: task.id, target: task.target }));
      }
      this.rowsEl.appendChild(row);
    }

    // Page dots
    this.dotsEl.innerHTML = '';
    PAGES.forEach((pg, i) => {
      const allDone = pg.tasks.every(t => state[t.id]);
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = `sdl-tb-dot${i === this.currentPage ? ' active' : ''}${allDone ? ' complete' : ''}`;
      dot.addEventListener('click', () => {
        this.userPicked = true;
        this.currentPage = i;
        this.render();
      });
      this.dotsEl.appendChild(dot);
    });
  }

  private toggleCollapse(): void {
    this.collapsed = !this.collapsed;
    localStorage.setItem(COLLAPSE_KEY, this.collapsed ? '1' : '0');
    this.applyCollapsed(true);
  }

  /** Apply collapse state. `animate=false` commits instantly (initial mount). */
  private applyCollapsed(animate: boolean): void {
    this.caretEl.textContent = this.collapsed ? '▸' : '▾';
    if (!animate) {
      this.bodyEl.style.transition = 'none';
      // Expanded → clear the inline cap so the stylesheet max-height applies
      // (scrollHeight is 0 while the board is still display:none at mount).
      this.bodyEl.style.maxHeight = this.collapsed ? '0px' : '';
      this.bodyEl.style.opacity = this.collapsed ? '0' : '1';
      // Commit, then restore transition on the next frame.
      void this.bodyEl.offsetHeight;
      requestAnimationFrame(() => { this.bodyEl.style.transition = ''; });
      return;
    }
    if (this.collapsed) {
      this.bodyEl.style.maxHeight = `${this.bodyEl.scrollHeight}px`;
      void this.bodyEl.offsetHeight;
      this.bodyEl.style.maxHeight = '0px';
      this.bodyEl.style.opacity = '0';
    } else {
      this.bodyEl.style.maxHeight = `${this.bodyEl.scrollHeight}px`;
      this.bodyEl.style.opacity = '1';
      // Release the fixed height after the open transition so content can reflow.
      window.setTimeout(() => {
        if (!this.collapsed) this.bodyEl.style.maxHeight = '';
      }, 320);
    }
  }

  private injectStyles(): void {
    if (document.getElementById('sdl-taskboard-styles')) return;
    const styles = document.createElement('style');
    styles.id = 'sdl-taskboard-styles';
    styles.textContent = `
      .sdl-taskboard {
        background: var(--glass);
        border: 1px solid var(--glass-border);
        border-radius: 14px;
        overflow: hidden;
        transition: opacity 0.4s ease, transform 0.4s ease;
      }
      .sdl-taskboard.sdl-tb-retiring {
        opacity: 0;
        transform: translateY(-8px) scale(0.98);
      }

      .sdl-tb-header {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        padding: 12px 14px;
        background: linear-gradient(135deg, rgba(255,215,0,0.10), rgba(255,215,0,0.02));
        border: none;
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;
        text-align: left;
      }
      .sdl-tb-titlewrap {
        display: flex;
        flex-direction: column;
        gap: 2px;
        flex: 1;
        min-width: 0;
      }
      .sdl-tb-kicker {
        font-size: 9px;
        letter-spacing: 1.5px;
        color: rgba(255,255,255,0.45);
        font-weight: 700;
      }
      .sdl-tb-title {
        font-family: var(--font-display);
        font-size: 17px;
        letter-spacing: 1.5px;
        color: var(--gold);
      }
      .sdl-tb-progress {
        font-family: var(--font-display);
        font-size: 15px;
        color: white;
        background: rgba(0,0,0,0.3);
        border: 1px solid var(--glass-border);
        border-radius: 12px;
        padding: 2px 10px;
      }
      .sdl-tb-caret {
        font-size: 12px;
        color: rgba(255,255,255,0.5);
        width: 14px;
        text-align: center;
      }

      .sdl-tb-body {
        max-height: 600px;
        opacity: 1;
        overflow: hidden;
        transition: max-height 0.3s ease, opacity 0.3s ease;
      }

      .sdl-tb-rows {
        display: flex;
        flex-direction: column;
        padding: 6px;
        gap: 4px;
      }
      .sdl-tb-row {
        display: flex;
        align-items: center;
        gap: 12px;
        width: 100%;
        padding: 10px 10px;
        background: rgba(255,255,255,0.02);
        border: 1px solid transparent;
        border-radius: 10px;
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;
        text-align: left;
        transition: background 0.15s, border-color 0.15s;
      }
      .sdl-tb-row:active { transform: scale(0.99); background: rgba(255,255,255,0.05); }
      .sdl-tb-row.done { cursor: default; opacity: 0.55; }

      .sdl-tb-icon {
        flex-shrink: 0;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        background: rgba(0,0,0,0.3);
        border: 1px solid var(--glass-border);
        border-radius: 9px;
      }
      .sdl-tb-row.done .sdl-tb-icon {
        background: rgba(0, 245, 160, 0.12);
        border-color: rgba(0, 245, 160, 0.5);
        color: var(--green, #00f5a0);
        font-weight: 700;
      }

      .sdl-tb-text { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0; }
      .sdl-tb-rowtitle {
        font-size: 14px;
        font-weight: 700;
        color: white;
        letter-spacing: 0.3px;
      }
      .sdl-tb-row.done .sdl-tb-rowtitle { text-decoration: line-through; text-decoration-color: rgba(255,255,255,0.3); }
      .sdl-tb-rowdesc {
        font-size: 11px;
        color: rgba(255,255,255,0.45);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .sdl-tb-arrow {
        flex-shrink: 0;
        font-size: 20px;
        color: rgba(255,255,255,0.3);
        width: 14px;
        text-align: center;
      }

      .sdl-tb-foot {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px 14px 12px;
        gap: 10px;
      }
      .sdl-tb-dots { display: flex; gap: 6px; }
      .sdl-tb-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: rgba(255,255,255,0.2);
        border: none;
        padding: 0;
        cursor: pointer;
        transition: background 0.2s, transform 0.2s;
      }
      .sdl-tb-dot.active { background: var(--gold); transform: scale(1.2); }
      .sdl-tb-dot.complete:not(.active) { background: var(--green, #00f5a0); }
      .sdl-tb-reward {
        font-size: 11px;
        color: rgba(255,255,255,0.5);
        font-weight: 600;
        letter-spacing: 0.3px;
      }
    `;
    document.head.appendChild(styles);
  }

  destroy(): void {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.element.remove();
    this.removeAllListeners();
  }
}
